// db-service.js - IndexedDB implementation for persistent user settings
// Handles ID Card settings, Email Signature settings, and Profile URL settings

// Database constants
const DB_NAME = 'UserPreferencesDB';
const DB_VERSION = 1;
const SETTINGS_STORE = 'userSettings';

// Setting keys for different components
const SETTINGS_KEYS = {
  ID_CARD: 'idCardPreferences',
  EMAIL_SIGNATURE: 'emailSignaturePreferences',
  PROFILE_URL: 'profileUrlPreferences'  // New key for Profile URL settings
};

// Initialize the database
export const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      reject('Error opening IndexedDB');
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      // Create the object store if it doesn't exist
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE, { keyPath: 'id' });
      }
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      resolve(db);
    };
  });
};

// Save settings to IndexedDB with specified key
export const saveSettings = async (settings, settingKey = SETTINGS_KEYS.ID_CARD) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([SETTINGS_STORE], 'readwrite');
      const store = transaction.objectStore(SETTINGS_STORE);
      
      // Prepare data object with an ID for storage
      const settingsObj = {
        id: settingKey,
        ...settings,
        timestamp: Date.now()
      };
      
      const request = store.put(settingsObj);
      
      request.onerror = (event) => {
        console.error(`Error saving ${settingKey}:`, event.target.error);
        reject(`Failed to save ${settingKey}`);
      };
      
      request.onsuccess = () => {
        resolve(`${settingKey} saved successfully`);
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error(`SaveSettings error for ${settingKey}:`, error);
    return null;
  }
};

// Load settings from IndexedDB with specified key
export const loadSettings = async (settingKey = SETTINGS_KEYS.ID_CARD) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([SETTINGS_STORE], 'readonly');
      const store = transaction.objectStore(SETTINGS_STORE);
      const request = store.get(settingKey);
      
      request.onerror = (event) => {
        console.error(`Error loading ${settingKey}:`, event.target.error);
        reject(`Failed to load ${settingKey}`);
      };
      
      request.onsuccess = (event) => {
        const result = event.target.result;
        resolve(result || null);
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error(`LoadSettings error for ${settingKey}:`, error);
    return null;
  }
};

// Clear settings with specified key
export const clearSettings = async (settingKey = SETTINGS_KEYS.ID_CARD) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([SETTINGS_STORE], 'readwrite');
      const store = transaction.objectStore(SETTINGS_STORE);
      const request = store.delete(settingKey);
      
      request.onerror = (event) => {
        console.error(`Error clearing ${settingKey}:`, event.target.error);
        reject(`Failed to clear ${settingKey}`);
      };
      
      request.onsuccess = () => {
        resolve(`${settingKey} cleared successfully`);
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error(`ClearSettings error for ${settingKey}:`, error);
    return null;
  }
};

// Fallback to cookies if IndexedDB is not available
const getCookieName = (settingKey) => `user_preferences_${settingKey}`;

export const saveToCookie = (settings, settingKey = SETTINGS_KEYS.ID_CARD) => {
  try {
    // Serialize and save settings as a cookie (expires in 30 days)
    const serializedSettings = JSON.stringify(settings);
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + 30);
    
    document.cookie = `${getCookieName(settingKey)}=${encodeURIComponent(serializedSettings)}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Strict`;
    return true;
  } catch (error) {
    console.error(`Error saving ${settingKey} to cookie:`, error);
    return false;
  }
};

export const loadFromCookie = (settingKey = SETTINGS_KEYS.ID_CARD) => {
  try {
    const cookieName = getCookieName(settingKey);
    const cookies = document.cookie.split(';');
    const settingsCookie = cookies.find(cookie => cookie.trim().startsWith(`${cookieName}=`));
    
    if (settingsCookie) {
      const serializedSettings = decodeURIComponent(settingsCookie.split('=')[1]);
      return JSON.parse(serializedSettings);
    }
    return null;
  } catch (error) {
    console.error(`Error loading ${settingKey} from cookie:`, error);
    return null;
  }
};

export const clearCookie = (settingKey = SETTINGS_KEYS.ID_CARD) => {
  document.cookie = `${getCookieName(settingKey)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// Helper for checking IndexedDB availability
export const isIndexedDBAvailable = () => {
  return !!window.indexedDB;
};

// Export the setting keys
export { SETTINGS_KEYS };